// Hand a recording's audio to the clinician as a file.
//
// The last backstop under everything else: the durable queue, the server rescue,
// the assembler's gap fill and the drain all exist so audio reaches the encounter
// by itself. When they have all failed — a permanently 404ing encounter, a save the
// backend keeps refusing, a recording about to be deleted from the queue — the
// audio still exists on this device, and the clinician should be able to walk away
// with it rather than watch it be discarded.
//
// Nothing leaves the device either way: no upload, no third party. Two ways of
// handing the file over —
//
//   1. A folder the clinician picks (File System Access API, Chromium only):
//      every write is awaited and its result is KNOWN. This is what Save all uses
//      whenever it can, because Save all is offered immediately before Delete all
//      and "saved" there has to be true.
//   2. A plain anchor download: fire-and-forget. `a.click()` not throwing means
//      the click was dispatched, not that a file landed. Chrome's "multiple
//      automatic downloads" block and Safari's user-gesture rule both drop
//      script-initiated downloads after the first without any signal to the page,
//      which is exactly the shape of a bulk save. Fine for one file the clinician
//      just clicked on; never proof for a batch.

// data: URL -> Blob, without a fetch() round trip (the assembled MP3 arrives as a
// base64 data URL and can be tens of megabytes).
export function dataUrlToBlob(dataUrl) {
  if (typeof dataUrl !== "string") return null;
  const comma = dataUrl.indexOf(",");
  if (!dataUrl.startsWith("data:") || comma < 0) return null;
  const header = dataUrl.slice(5, comma);
  const isBase64 = /;base64$/i.test(header);
  const mime = header.replace(/;base64$/i, "") || "application/octet-stream";
  const body = dataUrl.slice(comma + 1);
  try {
    if (!isBase64) return new Blob([decodeURIComponent(body)], { type: mime });
    const binary = atob(body);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch (e) {
    return null;
  }
}

// A filename the clinician can find again: who and when, no PHI beyond the patient
// name they already see on the row, and filesystem-safe.
export function audioFileName({ patient, dateMs, mimeType }) {
  const stamp = (() => {
    const d = new Date(Number(dateMs) || Date.now());
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}`;
  })();
  const who = String(patient || "")
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "_");
  const ext = /mpeg|mp3/i.test(mimeType || "") ? "mp3" : /mp4|m4a/i.test(mimeType || "") ? "m4a" : "webm";
  return ["recording", who, stamp].filter(Boolean).join("_") + "." + ext;
}

// Whether offering the save is honest here. Inside the React Native shell the audio
// file is already the app's own — it keeps it and lists it in its Local Recordings
// screen — and an anchor download does nothing in that WebView, so the button would
// be a lie. Everywhere else (browser, Chrome extension side panel) it works.
export function canSaveAudioToDevice() {
  try {
    if (typeof window === "undefined") return false;
    if (window.ReactNativeWebView !== undefined) return false;
    return typeof document !== "undefined" && !!window.URL?.createObjectURL;
  } catch (_) {
    return false;
  }
}

// Whether this browser can let the clinician pick a folder to save INTO (File
// System Access API — Chromium, including the extension side panel's iframe, though
// the picker there is refused at call time, see pickSaveDirectory). Firefox and
// Safari have no such thing and fall back to the anchor download.
export function canPickSaveDirectory() {
  try {
    return typeof window !== "undefined" && typeof window.showDirectoryPicker === "function";
  } catch (_) {
    return false;
  }
}

// Ask the clinician for a folder. Resolves to { handle, cancelled }:
//   { handle, cancelled: false }  — a writable directory handle
//   { handle: null, cancelled: true }  — they closed the picker (AbortError)
//   { handle: null, cancelled: false } — the picker is refused here (SecurityError
//     inside the extension's cross-origin iframe, NotAllowedError without a user
//     gesture, and so on); the caller falls back to anchor downloads.
//
// `showDirectoryPicker` is called SYNCHRONOUSLY — before any await, on the click's
// own call stack — because the browser only honours it under a live user
// activation. The invariant is that NO await runs between the click handler and
// this call: not here, and not in the caller ahead of it (saveAll calls this as
// its first statement). It is the absence of an await that keeps the activation
// alive, not the shape of the function — an `async` function's body runs
// synchronously up to its first `await` — so an `await ensureDb()` or a state
// flush added anywhere on that path gets NotAllowedError and no picker, whatever
// this function is declared as.
export function pickSaveDirectory() {
  const refused = (e) => ({ handle: null, cancelled: e?.name === "AbortError" });
  let request;
  try {
    request = window.showDirectoryPicker({
      // Ask for write permission up front, in one prompt, rather than once per
      // file inside the loop.
      mode: "readwrite",
      // Lets the browser reopen the folder picked last time for this purpose.
      id: "lucasai-recordings",
      startIn: "downloads",
    });
  } catch (e) {
    return Promise.resolve(refused(e));
  }
  return Promise.resolve(request).then((handle) => ({ handle: handle || null, cancelled: false }), refused);
}

// A free name in the folder: the requested one, else `name_2`, `name_3`, … A probe
// WITHOUT `create` is the only way to ask "is this taken?" — getFileHandle with
// create:true on an existing file hands back that file and the writable then
// truncates it, i.e. silently overwrites the last save. NotFoundError is the one
// answer that means free; a directory of that name (TypeMismatchError) is taken;
// anything else (permission revoked mid-run) is rethrown so the write fails
// honestly instead of looping.
async function freeFileName(dir, fileName) {
  const dot = fileName.lastIndexOf(".");
  const base = dot > 0 ? fileName.slice(0, dot) : fileName;
  const ext = dot > 0 ? fileName.slice(dot) : "";
  let candidate = fileName;
  for (let n = 2; n < 1000; n++) {
    try {
      await dir.getFileHandle(candidate);
    } catch (e) {
      if (e?.name === "NotFoundError") return candidate;
      if (e?.name !== "TypeMismatchError") throw e;
    }
    candidate = `${base}_${n}${ext}`;
  }
  return `${base}_${Date.now()}${ext}`;
}

// Write one blob into the picked folder. Resolves true only once the file has been
// written AND closed — the close is what commits the swap file the browser was
// writing to, so a true here means bytes on disk. Never throws.
export async function writeAudioToDirectory(handle, blob, fileName) {
  let writable = null;
  try {
    if (!handle || !blob) return false;
    const name = await freeFileName(handle, fileName || "recording.webm");
    const file = await handle.getFileHandle(name, { create: true });
    writable = await file.createWritable();
    await writable.write(blob);
    await writable.close();
    writable = null;
    return true;
  } catch (e) {
    // A stream that was opened but not closed would leave the swap file behind
    // until it is collected; abort discards it now. Best effort only.
    if (writable) {
      try {
        await writable.abort();
      } catch (_) {
        /* already closed or errored */
      }
    }
    console.error("Could not write the recording audio into the chosen folder", e?.name || e);
    return false;
  }
}

// Returns false rather than throwing: the caller is already on a failure path and
// must be able to tell the clinician the save did not happen.
//
// NOTE the true here is weaker than writeAudioToDirectory's: it means the download
// was HANDED to the browser, not that it completed (see the header comment).
export function saveAudioFile(blob, fileName) {
  try {
    if (!blob || typeof document === "undefined" || !window.URL?.createObjectURL) return false;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName || "recording.webm";
    // Appended so Firefox honours the click; removed immediately after.
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Revoked on a later task: revoking synchronously can cancel the download the
    // click just started in some engines.
    setTimeout(() => URL.revokeObjectURL(url), 10000);
    return true;
  } catch (e) {
    console.error("Could not save the recording audio to a file", e?.name || e);
    return false;
  }
}
